
import { memo } from 'react';
import { Handle, Position } from '@xyflow/react';
import { XCircle, Settings } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useNodeSettings } from '../NodeSettingsContext';
import { type StateNodeData } from '@/types/workflow-graph';
import { cn } from '@/lib/utils';

interface FailNodeProps {
    data: StateNodeData;
    selected?: boolean;
}

export const FailNode = memo(function FailNode({ data, selected }: FailNodeProps) {
    const { openSettings } = useNodeSettings();
    const { layoutDirection = 'LR' } = data;
    
    // Determine handle position based on layout direction
    const isHorizontal = layoutDirection === 'LR';
    const inputPosition = isHorizontal ? Position.Left : Position.Top;
    
    return (
        <div className="relative w-fit">
            {/* Settings button - positioned outside the circle */}
            <div className="absolute -right-5 -top-1 z-10">
                <Button
                    variant="ghost"
                    size="icon"
                    className="h-5 w-5 rounded-full bg-white text-slate-400 shadow-sm border border-slate-200 hover:bg-slate-100 hover:text-slate-600"
                    title="Node Settings"
                    onClick={(e) => {
                        e.stopPropagation();
                        openSettings(data);
                    }}
                >
                    <Settings size={10} />
                </Button>
            </div>

            <div
                className={cn(
                    'flex h-20 w-20 items-center justify-center rounded-full border-2 border-red-300 bg-red-50',
                    selected && 'ring-2 ring-blue-500 ring-offset-2'
                )}
            >
                {/* Input Handle - position based on layout direction */}
                <Handle
                    type="target"
                    id="target"
                    position={inputPosition}
                    className="!h-3 !w-3 !border-red-500 !bg-red-400"
                />
                <div className="text-center">
                    <XCircle className="mx-auto h-6 w-6 text-red-600" />
                    <span className="mt-1 block text-xs font-medium text-red-700">{data.label}</span>
                </div>
            </div>
        </div>
    );
});
